import { useCallback, useMemo, useRef } from 'react';
import sortBy from 'lodash/sortBy';

import {
  FillInBlanksProperties,
  FillInBlanksSentence,
  FillInBlanksText,
  SettingsProps,
} from '../fill-in-blanks.constants';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { DIRECTION_LEFT } from 'webclient.constants';
import { MultiViewProps } from 'approot/shared/layout/layout-multi-view';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import customNanoId from 'lib/nanoid/nanoid';
import {
  FILL_IN_BLANKS_SENTENCE_DETAILS,
  FILL_IN_BLANKS_SENTENCES,
} from './fill-in-blanks-settings.constants';
import { useFillInBlanksSettingsContext } from './fill-in-blanks-settings.context';
import {
  Sortable,
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { SortableContentButton } from 'approot/shared/sortable/sortable.style';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { COLOUR_GREY } from 'approot/shared/colour/colour.constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import { mergeFillInBlanksBlocks } from '../logic/merge-fill-in-blanks-blocks';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { NegativeButton } from 'approot/shared/styles/buttons';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import useFillInBlanksDeleteSentence from './use-fill-in-blanks-delete-sentence';
import FillInBlanksSettingsTextField from './fill-in-blanks-settings-text-field';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

const FillInBlanksSettingsSentence = ({
  id,
  sectionId,
  properties,
  suggestedAnswerProperties,
  goTo,
}: SettingsProps & MultiViewProps) => {
  const ref = useRef<HTMLElement | null>(null);

  const { fillInBlanks } = properties;

  const { updateComponentProperties } = useBatchComponentUpdateContext();
  const { activeSentence, setActiveAnswer } = useFillInBlanksSettingsContext();

  const { setEditModeTargetElement } = useActivityBuilderContext();
  const {
    isVisible: isDeleteSentenceModalVisible,
    openModal: openDeleteSentenceModal,
    closeModal: closeDeleteSentenceModal,
  } = useSimpleModalState();

  const { handleDeleteSentence } = useFillInBlanksDeleteSentence({
    id,
    sectionId,
    properties,
    suggestedAnswerProperties,
  });

  const sentence = useMemo(
    () => fillInBlanks.sentences.find(s => s.itemId === activeSentence.itemId),
    [activeSentence, fillInBlanks.sentences]
  );

  const sentenceIndex = useMemo(
    () =>
      fillInBlanks.sentences.findIndex(s => s.itemId === activeSentence.itemId),
    [activeSentence, fillInBlanks.sentences]
  );

  const sortedTextBlocks = useMemo(
    () => sortBy(sentence?.textBlocks, 'sort'),
    [sentence]
  );

  const sortedAnswerBlocks = useMemo(
    () => sortBy(sentence?.answerBlocks, 'sort'),
    [sentence]
  );

  const sortedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  const handleAddText = useCallback(() => {
    const updatedSentences: FillInBlanksSentence[] =
      properties.fillInBlanks.sentences.map(s => {
        return s.itemId === activeSentence.itemId && sentence
          ? {
              ...s,
              textBlocks: [
                ...s.textBlocks,
                {
                  __typename: 'FillInBlanksText',
                  itemId: customNanoId(),
                  sort: getReorderSort(
                    sortedBlocks,
                    sortedBlocks.length,
                    DIRECTION_LEFT
                  ),
                  text: {
                    type: 'doc',
                    content: [
                      {
                        type: 'paragraph',
                        content: [
                          {
                            type: 'text',
                            text: 'Text',
                          },
                        ],
                      },
                    ],
                  },
                },
              ],
            }
          : s;
      });

    updateComponentProperties({
      input: {
        componentId: id,
        sectionId,
        componentData: {
          properties: {
            fillInBlanks: {
              ...properties.fillInBlanks,
              sentences: updatedSentences,
            },
          },
        },
      },
      properties: {
        ...properties,
        fillInBlanks: {
          ...properties.fillInBlanks,
          sentences: updatedSentences,
        },
      },
      typename: 'ActivitiesFillInBlanksResponseComponent',
    });
  }, [
    id,
    sectionId,
    properties,
    activeSentence.itemId,
    sortedBlocks,
    sentence,
    updateComponentProperties,
  ]);

  const handleAddSpace = useCallback(() => {
    const updatedSentences: FillInBlanksSentence[] =
      properties.fillInBlanks.sentences.map(s => {
        if (s.itemId === activeSentence.itemId && sentence) {
          return {
            ...s,
            answerBlocks: [
              ...s.answerBlocks,
              {
                __typename: 'FillInBlanksAnswer',
                itemId: customNanoId(),
                sort: getReorderSort(
                  sortedBlocks,
                  sortedBlocks.length,
                  DIRECTION_LEFT
                ),
                colour: COLOUR_GREY.base,
                label: null,
                placeholder: null,
                punctuation: null,
                showLabel: false,
                showPlaceholderAsScaffold: false,
                width: 100,
              },
            ],
          };
        }
        return s;
      });
    updateComponentProperties({
      input: {
        componentId: id,
        sectionId,
        componentData: {
          properties: {
            fillInBlanks: {
              ...properties.fillInBlanks,
              sentences: updatedSentences,
            },
          },
        },
      },
      properties: {
        ...properties,
        fillInBlanks: {
          ...properties.fillInBlanks,
          sentences: updatedSentences,
        },
      },
      typename: 'ActivitiesFillInBlanksResponseComponent',
    });
  }, [
    id,
    sectionId,
    properties,
    activeSentence.itemId,
    sentence,
    sortedBlocks,
    updateComponentProperties,
  ]);

  const handleReorderItem = useCallback(
    (itemId: string, sort: string) => {
      const updatedSentences: FillInBlanksSentence[] =
        properties.fillInBlanks.sentences.map(sentence => {
          if (sentence.itemId === activeSentence.itemId) {
            return {
              ...sentence,
              answerBlocks: sortedAnswerBlocks.map(a => {
                if (a.itemId === itemId) {
                  return {
                    ...a,
                    sort,
                  };
                }
                return a;
              }),
              textBlocks: sortedTextBlocks.map(a => {
                if (a.itemId === itemId) {
                  return {
                    ...a,
                    sort,
                  };
                }
                return a;
              }),
            };
          }
          return sentence;
        });

      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences: updatedSentences,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            sentences: updatedSentences,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [
      id,
      sectionId,
      sortedAnswerBlocks,
      sortedTextBlocks,
      activeSentence.itemId,
      properties,
      updateComponentProperties,
    ]
  );

  const handleUpdateText = useCallback(
    (updatedText: FillInBlanksText) => {
      const updatedSentences: FillInBlanksSentence[] =
        properties.fillInBlanks.sentences.map(sentence => {
          if (sentence.itemId === activeSentence.itemId) {
            return {
              ...sentence,
              textBlocks: sentence.textBlocks.map(answer =>
                answer.itemId === updatedText.itemId ? updatedText : answer
              ),
            };
          }
          return sentence;
        });
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences: updatedSentences,
              },
            },
          },
        },
        properties: {
          ...properties,
          fillInBlanks: {
            ...properties.fillInBlanks,
            sentences: updatedSentences,
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
      });
    },
    [
      id,
      sectionId,
      properties,
      activeSentence.itemId,
      updateComponentProperties,
    ]
  );

  return (
    <ActivityBuilderDrawerWrapper>
      {isDeleteSentenceModalVisible && (
        <ActivityBuilderModal
          isVisible
          title="Delete Sentence"
          headerTitle="Delete Sentence"
          actions={
            <NegativeButton
              onClick={() => {
                handleDeleteSentence(activeSentence.itemId);
                closeDeleteSentenceModal();
                goTo(FILL_IN_BLANKS_SENTENCES);
                setEditModeTargetElement(undefined);
              }}
            >
              Delete Sentence
            </NegativeButton>
          }
          onClose={closeDeleteSentenceModal}
        >
          <PStyled>Are you sure you want to delete this sentence?</PStyled>
        </ActivityBuilderModal>
      )}
      <ActivityBuilderDrawerHeader
        isBack
        title={`Sentence ${sentenceIndex + 1}`}
        onClose={() => {
          goTo(FILL_IN_BLANKS_SENTENCES);
          setEditModeTargetElement(undefined);
        }}
        headerButton={
          <DeleteButtonIcon
            $isSmall
            aria-label="Delete Sentence"
            onClick={openDeleteSentenceModal}
            data-selector="icon-delete-sentence"
          />
        }
      />
      <ActivityBuilderDrawerContent ref={ref}>
        <PStyled>Add text and blanks below to build your sentence.</PStyled>
        <SettingsGroup marginBottom={0}>
          <Sortable
            list={sortedBlocks}
            renderItem={(_, index) => {
              const sourceItem = sortedBlocks[index];
              if (!sourceItem) return null;

              const deleteComp = (
                <DeleteItem
                  itemId={sourceItem.itemId}
                  type={'text' in sourceItem ? 'text' : 'answer'}
                  properties={properties}
                  id={id}
                  sectionId={sectionId}
                />
              );

              const comp =
                'text' in sourceItem ? (
                  <SortableItem key={sourceItem.itemId}>
                    <SortableHeader>{deleteComp}</SortableHeader>
                    <SortableContent>
                      <FillInBlanksSettingsTextField
                        initialValue={sourceItem.text}
                        onChange={json =>
                          handleUpdateText({
                            ...sourceItem,
                            text: json,
                          })
                        }
                      />
                    </SortableContent>
                  </SortableItem>
                ) : (
                  <SortableItem key={sourceItem.itemId}>
                    <SortableHeader>{deleteComp}</SortableHeader>
                    <SortableContentButton
                      type="button"
                      onClick={() => {
                        setActiveAnswer({
                          itemId: sourceItem.itemId,
                        });
                        setEditModeTargetElement(sourceItem.itemId);
                        goTo(FILL_IN_BLANKS_SENTENCE_DETAILS);
                      }}
                    >
                      <Box $alignItems="center" $justifyContent="space-between">
                        {`Blank Space ${
                          sortedAnswerBlocks.findIndex(
                            as => as.itemId === sourceItem.itemId
                          ) + 1
                        }`}
                        <IconDropdown width={16} height={16} rotate={-90} />
                      </Box>
                    </SortableContentButton>
                  </SortableItem>
                );
              return comp;
            }}
            onSortChange={({ sourceIndex, destinationIndex, direction }) => {
              if (sourceIndex !== destinationIndex) {
                const item = sortedBlocks[sourceIndex];
                const newSort = getReorderSort(
                  sortedBlocks,
                  destinationIndex,
                  direction
                );
                handleReorderItem(item.itemId, newSort);
              }
            }}
          />
        </SettingsGroup>

        <SettingsGroup>
          <Box $gap={16} $flexWrap="nowrap">
            <SecondaryButton
              type="button"
              onClick={handleAddText}
              data-selector="btn-add-text"
            >
              Add Text
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={handleAddSpace}
              data-selector="btn-add-space"
            >
              Add Blank Space
            </SecondaryButton>
          </Box>
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default FillInBlanksSettingsSentence;

const DeleteItem = ({
  id,
  properties,
  sectionId,
  itemId,
  type,
}: {
  id: string;
  properties: FillInBlanksProperties;
  sectionId: string;
  itemId: string;
  type: 'text' | 'answer';
}) => {
  const {
    isVisible: isDeleteItemModalVisible,
    openModal: openDeleteItemModal,
    closeModal: closeDeleteItemModal,
  } = useSimpleModalState();

  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();
  const { activeSentence } = useFillInBlanksSettingsContext();
  const { showError } = useSnackbar();

  const handleDelete = useCallback(
    (itemId: string) => {
      const updatedSentences: FillInBlanksSentence[] =
        properties.fillInBlanks.sentences.map(s => {
          if (s.itemId === activeSentence.itemId) {
            return {
              ...s,
              answerBlocks: s.answerBlocks.filter(a => a.itemId !== itemId),
              textBlocks: s.textBlocks.filter(a => a.itemId !== itemId),
            };
          }
          return s;
        });
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              fillInBlanks: {
                ...properties.fillInBlanks,
                sentences: updatedSentences,
              },
            },
          },
        },
        typename: 'ActivitiesFillInBlanksResponseComponent',
        isSaveImmediate: true,
        onCompleted: () => {
          closeDeleteItemModal();
        },
        onError: () => {
          closeDeleteItemModal();
          showError(
            'There was an error deleting the item. Please refresh and try again.'
          );
        },
      });
    },
    [
      id,
      sectionId,
      properties,
      activeSentence.itemId,
      updateComponentProperties,
      closeDeleteItemModal,
      showError,
    ]
  );

  const typeLabel = type === 'text' ? 'Text' : 'Blank Space';
  const modalTitle = `Delete ${typeLabel}`;

  return (
    <>
      {isDeleteItemModalVisible && (
        <ActivityBuilderModal
          isVisible
          title={modalTitle}
          headerTitle={modalTitle}
          actions={
            <NegativeButton
              loading={loading}
              disabled={loading}
              onClick={() => handleDelete(itemId)}
              data-selector="modal-btn-delete-item"
            >
              Delete {typeLabel}
            </NegativeButton>
          }
          onClose={closeDeleteItemModal}
        >
          <PStyled>
            Are you sure you want to delete this {typeLabel} item?
          </PStyled>
        </ActivityBuilderModal>
      )}
      <DeleteButtonIcon
        $isSmall
        aria-label={`Delete ${typeLabel}`}
        onClick={openDeleteItemModal}
        data-selector={`icon-delete-${typeLabel}`}
      />
    </>
  );
};
